import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getDismissedGuides, dismissGuide } from '../utils/api';
import logger from '../utils/logger';

// Utility to fetch guide content from static JSON
async function fetchGuideContent(guideKey) {
  const response = await fetch('/guides.json');
  const guides = await response.json();
  return guides[guideKey] || null;
}

const GuideModal = ({ guideKey }) => {
  const [guide, setGuide] = useState(null);
  const [show, setShow] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function checkDismissed() {
      let dismissed = [];
      // Always check localStorage first for immediate feedback
      let localDismissed = JSON.parse(localStorage.getItem('dismissedGuides') || '[]');
      logger.log('[GuideModal] guideKey:', guideKey);
      logger.log('[GuideModal] local dismissed guides:', localDismissed);
      
      // If the guide is already dismissed locally, don't show it
      // This provides immediate feedback even if the backend call is slow
      if (localDismissed.includes(guideKey)) {
        logger.log('[GuideModal] guideKey is dismissed locally, modal will not show:', guideKey);
        if (isMounted) setShow(false);
        return;
      }
      
      try {
        // Try to get dismissed guides from backend
        dismissed = await getDismissedGuides();
        logger.log('[GuideModal] dismissed guides from backend (raw):', dismissed);
        
        // ENHANCED DEBUGGING: Check if dismissed guides is an array
        if (!Array.isArray(dismissed)) {
          logger.error('[GuideModal] Backend returned non-array for dismissed guides:', dismissed);
          dismissed = [];
        }
        
        // MIGRATION: If backend returns empty but localStorage has values, migrate
        if (dismissed.length === 0 && localDismissed.length > 0) {
          logger.log('[GuideModal] Migrating localStorage dismissed guides to backend:', localDismissed);
          for (const key of localDismissed) {
            try {
              const result = await dismissGuide(key);
              logger.log(`[GuideModal] Migrated guideKey to backend:`, key);
              logger.log('[GuideModal] Migration response:', result);
            } catch (e) {
              logger.warn(`[GuideModal] Migration failed for guideKey:`, key, e);
              continue;
            }
          }
          // After migration, clear localStorage and re-fetch backend
          localStorage.removeItem('dismissedGuides');
          dismissed = await getDismissedGuides();
          logger.log('[GuideModal] After migration, backend dismissed guides:', dismissed);
        }
        
        // Merge backend and local dismissed guides
        const mergedDismissed = [...new Set([...dismissed, ...localDismissed])];
        logger.log('[GuideModal] Merged dismissed guides:', mergedDismissed);
        
        // If the guide is dismissed in either backend or localStorage, don't show it
        if (!isMounted) return;
        if (mergedDismissed.includes(guideKey)) {
          logger.log('[GuideModal] guideKey is dismissed, modal will not show:', guideKey);
          setShow(false);
          return;
        } else {
          logger.log('[GuideModal] guideKey is NOT dismissed, modal will show:', guideKey);
        }
      } catch (e) {
        // fallback to localStorage if backend fails (not logged in, etc)
        dismissed = localDismissed;
        logger.warn('[GuideModal] Backend fetch failed, using localStorage dismissed guides:', dismissed);
        
        // If the guide is dismissed in localStorage, don't show it
        if (!isMounted) return;
        if (dismissed.includes(guideKey)) {
          logger.log('[GuideModal] guideKey is dismissed locally, modal will not show:', guideKey);
          setShow(false);
          return;
        }
      }
      
      // If we get here, the guide is not dismissed, so fetch and show it
      if (!isMounted) return;
      fetchGuideContent(guideKey).then(content => {
        logger.log('[GuideModal] fetched guide content:', content);
        if (content && isMounted) {
          setGuide(content);
          setShow(true);
        }
      });
    }
    checkDismissed();
    return () => { isMounted = false; };
  }, [guideKey]);

  const handleClose = async () => {
    setShow(false);
    if (dontShowAgain) {
      logger.log('[GuideModal] Dismissing guide:', guideKey);
      
      // Always update localStorage first for immediate feedback on next page load
      const localDismissed = JSON.parse(localStorage.getItem('dismissedGuides') || '[]');
      if (!localDismissed.includes(guideKey)) {
        localDismissed.push(guideKey);
        localStorage.setItem('dismissedGuides', JSON.stringify(localDismissed));
        logger.log('[GuideModal] Updated localStorage dismissed guides:', localDismissed);
      }
      
      try {
        // Then update the backend
        const result = await dismissGuide(guideKey);
        logger.log('[GuideModal] Successfully dismissed guide in backend:', guideKey);
        logger.log('[GuideModal] Backend response after dismissal:', result);
        
        // ENHANCED DEBUGGING: Force refresh of localStorage to ensure it's up to date
        const updatedLocalDismissed = JSON.parse(localStorage.getItem('dismissedGuides') || '[]');
        logger.log('[GuideModal] Current localStorage after dismissal:', updatedLocalDismissed);
      } catch (e) {
        logger.warn('[GuideModal] Failed to dismiss guide in backend, but it is saved in localStorage:', e);
        // No need for fallback as we've already updated localStorage
      }
    }
  };

  if (!show || !guide) return null;

  return (
    <div className="guide-modal-overlay">
      <div className="guide-modal">
        <h2 className="guide-modal-title">{guide.title}</h2>
        <div className="guide-modal-content">
          <div dangerouslySetInnerHTML={{ __html: guide.description }} />
          {guide.image && <img src={guide.image} alt="Guide visual" style={{ maxWidth: '100%' }} />}
          {guide.video && (
            <div style={{ marginTop: 16 }}>
              <iframe
                width="100%"
                height="315"
                src={guide.video}
                title="Guide Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
        <div className="guide-modal-footer">
          <label>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
            />
            Don't show again
          </label>
          <button onClick={handleClose}>Close</button>
        </div>
      </div>
      <style>{`
        .guide-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.4);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 15px;
          overflow-y: auto;
        }
        .guide-modal {
          background: #fff;
          border-radius: 12px;
          max-width: 480px;
          width: 100%;
          padding: 24px 20px 16px 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .guide-modal-title {
          text-align: center;
          font-weight: bold;
          font-size: 1.75rem;
          margin-bottom: 16px;
        }
        .guide-modal-content {
          overflow-y: auto;
          padding-right: 5px;
          flex: 1;
          -webkit-overflow-scrolling: touch;
        }
        .guide-modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 20px;
          padding-top: 12px;
          border-top: 1px solid #eee;
          flex-wrap: wrap;
          gap: 10px;
          position: sticky;
          bottom: 0;
          background: #fff;
        }
        .guide-modal-footer label {
          display: flex;
          align-items: center;
          font-size: 14px;
          user-select: none;
          padding: 8px 0;
        }
        .guide-modal-footer input[type="checkbox"] {
          margin-right: 8px;
          width: 18px;
          height: 18px;
        }
        .guide-modal-footer button {
          padding: 10px 20px;
          background: #4a90e2;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: bold;
          cursor: pointer;
          font-size: 16px;
          min-width: 100px;
        }
        @media (max-width: 480px) {
          .guide-modal {
            padding: 20px 16px 12px 16px;
            width: 100%;
            max-height: 85vh;
          }
          .guide-modal-title {
            font-size: 1.5rem;
            margin-bottom: 12px;
          }
          .guide-modal-footer {
            flex-direction: column-reverse;
            align-items: stretch;
          }
          .guide-modal-footer label {
            margin-top: 8px;
            justify-content: center;
          }
          .guide-modal-footer button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

GuideModal.propTypes = {
  guideKey: PropTypes.string.isRequired
};

export default GuideModal;
